import styled, { createGlobalStyle } from 'styled-components';

import { COLORS, FIXED_HEADER, GRADIENTS, HEADER_DESKTOP_PX, NAV_DESKTOP } from '../../theme/tokens';
import { thinScrollbar } from '../../theme/mixins';

/**
 * The top bar lives in two different layout regimes, and the scrim has to match
 * each one:
 *
 *  - DESKTOP (> 899px): the header (`.pg-body-top`) is an in-flow flex row and
 *    the content is tucked up behind it. We fade it with an `absolute` scrim
 *    anchored to the content column (`HeaderScrim`), so it never touches the
 *    sidebar / Lumio panel and stays in lockstep with the tucked content.
 *  - MOBILE (<= 899px): the config makes `.pg-body-top` a `position: fixed`,
 *    solid-`#000` overlay. There the fade has to BE that header's background
 *    (a fixed overlay the content scrolls under), and the content must be padded
 *    down past it — there's no tuck.
 *
 * The 899px breakpoint at which the remote config flips the header to fixed comes
 * from the shared `FIXED_HEADER` token (imported above), so all the header-related
 * responsive rules key off it (not the area's general 820px reflow `MOBILE`) and it
 * stays in lockstep with the token's `MOBILE_MQ` sibling instead of a local literal.
 */

/** Desktop scrim height + the gap the content rests below it. Single source of the desktop header
 *  constant (shared with the immersive canvas editor's height anchor), so the tuck / scrim / composer
 *  math can never drift apart. */
const SCRIM_PX = HEADER_DESKTOP_PX;
const CONTENT_GAP_PX = 24;
/** Mobile fixed-header height (measured: `.pg-body-top` is 78px tall) + its gap. */
const MOBILE_HEADER_PX = 78;
const MOBILE_GAP_PX = 16;
/** Mobile fixed bottom-nav clearance so content isn't cut off behind it
 * (bar height ~104px + 30px breathing buffer). */
const MOBILE_NAV_PX = 134;
/** Horizontal inset of the content column, same on both regimes. */
const GUTTER_PX = 16;

/**
 * The hand-written nova pages render into the shared config-driven
 * layout, which wraps the page-content slot in a card (background + border).
 * These pages own their own surfaces, so that wrapper card is redundant here.
 *
 * The card isn't in this repo's source (it comes from remote config), so we
 * neutralise it from the page side: every page roots its content in
 * `ModuleElementDiv`, tagged `.ai-bb-page-root`, and we strip the background and
 * border off whatever element is its direct parent — i.e. the card slot. The
 * `> ` direct-child match keeps this scoped to that single wrapper, so the
 * layout chrome (left menu, top bar, Lumio panel) is never touched.
 */
export const LayoutBackdropReset = createGlobalStyle`
  *:has(> .ai-bb-page-root) {
    background: transparent !important;
    border: none !important;
  }
  /* Hand-written chrome (layouts/MainLayout): the page root sits a few wrappers
     (transition / brand-fade layers) below the content card, so the direct-child
     match above never reaches it, so strip the card itself whenever it hosts a
     nova page anywhere inside. */
  .pg-body-content:has(.ai-bb-page-root) {
    background: transparent !important;
    border: none !important;
  }
`;

/**
 * DESKTOP scrim. A fully-opaque-page-bg-to-transparent gradient that fades the
 * content as it scrolls up behind the in-flow header. It's `absolute`, anchored
 * to the content column (`.pg-body-content`, made `position: relative` by
 * `ContentTuck`) — so it's confined to that column (never over the sidebar or
 * the right-hand Lumio panel) and stays in lockstep with the tucked content.
 *
 * `z-index: 1` sits it above the scrolling content but below the header. On
 * mobile the header itself carries the scrim (see `ContentTuck`), so this one
 * is hidden.
 */
export const HeaderScrim = styled.div.attrs({ className: 'ai-bb-header-scrim' })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  /* Cover the full header band (${SCRIM_PX}px) AND the content gap below it
     (${CONTENT_GAP_PX}px), so the scrim is solid across the whole header and only
     fades once it's clear of it — the fade ends exactly at the content rest line
     (${SCRIM_PX} + ${CONTENT_GAP_PX}px), leaving resting content untouched. The
     plain ${SCRIM_PX}px scrim faded within the header band, leaking bright content
     (e.g. the Product cards' green bars) through the header's lower edge. */
  height: calc(${SCRIM_PX + CONTENT_GAP_PX}px + env(safe-area-inset-top));
  pointer-events: none;
  background: ${GRADIENTS.headerScrimDesktop};

  ${FIXED_HEADER} {
    display: none;
  }
`;

/**
 * Header + content-column setup, per regime.
 *
 *  - The header is forced to `z-index: 30` (the value the config already uses on
 *    mobile) so it reliably paints above the content and the desktop scrim on
 *    both breakpoints. We do NOT make `.pg-body-content` a stacking context —
 *    doing so traps the floating Lumio panel inside it (that's what made the
 *    Lumio card disappear).
 *  - DESKTOP: pull the content column up behind the in-flow header so content
 *    scrolls under it (its scroll-clip edge then sits off-screen, hidden). We own
 *    this rather than trusting the config's own negative margin, which is
 *    inconsistent across pages. `position: relative` anchors the `HeaderScrim`.
 *  - MOBILE: the header is a fixed overlay, so there's no tuck (margin 0); the
 *    content pads down past it (`Page`). We swap the header's solid `#000`
 *    background for the scrim gradient so content fades under it instead of
 *    hitting a hard black edge.
 */
export const ContentTuck = createGlobalStyle`
  .pg-body-top {
    z-index: 30 !important;
  }
  .pg-body-content {
    position: relative !important;
    margin-top: calc(-${SCRIM_PX}px - env(safe-area-inset-top)) !important;
  }
  ${FIXED_HEADER} {
    /* The header is a fixed overlay (takes no layout space). The config clears it
       by padding a layout ancestor down — but that stacks on top of the Page's own
       top padding, producing a huge gap, and it keeps the scroll area starting
       *below* the header so content hard-clips instead of scrolling behind it.
       Zero the top clearance on every ancestor so the scroll area reaches the very
       top (content scrolls behind the header, the gradient fades it), and let the
       Page's top padding be the single source of clearance.

       .pg-body also gets a 12px horizontal gutter from the config here — drop it
       entirely, so the Page column's own ${GUTTER_PX}px is the single source of the
       horizontal inset rather than the two stacking. */
    .pg-layout {
      padding-top: 0 !important;
    }
    .pg-body {
      padding: 0 !important;
    }
    .pg-body-content {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    .pg-body-top {
      background: ${GRADIENTS.headerScrim} !important;
    }
  }
`;

/**
 * Outer element that receives the `styleText` / `className` from ModuleElement.
 *
 * The app shell pins `html, body, #root` to `height: 100%` (see `src/index.scss`),
 * so the document never grows — a tall page must scroll *inside* its own slot.
 * We therefore fill the slot and own the vertical scroll here. `styleText` is
 * applied last, so page config can still override any of this.
 */
export const ModuleElementDiv = styled.div<{ $styleText: string }>`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  /* Same thin scrollbar as the Lumio thread (shared mixin). Before $styleText so
   * a page's own styleText can still override it if it ever needs to. */
  ${thinScrollbar}
  /* Desktop only: this is the studio's single vertical scroller, so reserve its 6px
   * gutter permanently. Without it the thin bar is carved out of / added back to the
   * content box whenever page height crosses the viewport (tab switch, a modal-driven
   * re-render), sliding the centered column ~6px sideways. Scoped to NAV_DESKTOP because
   * mobile hides scrollbars (index.scss) and scrolls the document, not this slot — there
   * is no gutter to stabilise. Before $styleText so a page can still override. */
  ${NAV_DESKTOP} {
    scrollbar-gutter: stable;
  }
  ${(props) => props.$styleText}
`;

/**
 * Center content column. Only the center of the screen is owned here — the left
 * menu, top bar (title/balance) and right-hand Lumio panel all come from the
 * layout (see CLAUDE.md). Sections stack vertically with a consistent gap.
 *
 * Top padding clears the header on each regime: on desktop it adds the tuck back
 * plus a gap (content rests `CONTENT_GAP_PX` below the header); on mobile it
 * clears the fixed overlay header (`MOBILE_HEADER_PX`) plus a gap.
 *
 * The horizontal inset is a token 16px on both regimes, replacing the 26/14px pair
 * that differed between them for no reason. This is area-wide (every page roots
 * in this shell), so a page wanting more breathing room adds it to its own sections
 * rather than widening this.
 */
export const Page = styled.div.attrs({ className: 'ai-bb-page-col' })`
  color: ${COLORS.text};
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
  padding: calc(${SCRIM_PX + CONTENT_GAP_PX}px + env(safe-area-inset-top)) ${GUTTER_PX}px 36px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: inherit;
  ${FIXED_HEADER} {
    /* Clear the fixed header AND, when a support agent is impersonating, the strip
       above it: --ows-imp-offset is the strip's measured height (0 otherwise) and it
       already owns the notch inset, so --ows-imp-safe-top (set to 0 by MainLayout while
       the strip is up) drops this padding's own safe-area to avoid a double inset. */
    padding: calc(
        ${MOBILE_HEADER_PX + MOBILE_GAP_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) +
          var(--ows-imp-offset, 0px)
      )
      ${GUTTER_PX}px calc(${MOBILE_NAV_PX}px + env(safe-area-inset-bottom));
    gap: 14px;
  }
`;
